import React, { useState, useEffect, useCallback } from 'react';
import { compose, bindActionCreators } from 'redux';
import { useDispatch, connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Description, List } from '@mui/icons-material';
import ContractSummary from './ContractSummary';
import ContractComponent from './ContractComponent';
import ContractSelectionContainer from './list/ContractSelectionContainer';
import { withTranslation } from 'react-i18next';
import { getOfferType } from 'features/offer/edit/client/form/selectors';
import { loadLeasingOfferContractData } from '../actionCreators';
import { loadOfferTypes } from 'actions/actionCreators/dictionariesActions';
import ContractDetailInfoComponent from './ContractDetailInfoComponent';
import { emptyString, emptyObject } from 'constants/emptyConst';
import { getDocumentSubtypes } from 'selectors/dictionariesSelectors';
import documentTypeIds from 'constants/documentTypeIds';
import { CollapseCard } from '@totalsoft/rocket-ui';

const ContractCard = ({
  isNewContractModification,
  t,
  openContractSelection,
  closeContractSelection,
  showContractSelection,
  documentId,
  siteId,
  actions,
  modificationTypes,
  offerTypeId,
  onChangeOpenReinitializationConfirmBool,
  reinitializationProcessing
}) => {
  const [modificationName, setModificationName] = useState(emptyString);
  const [contractCardExpanded, setContractCardExpanded] = useState(isNewContractModification);
  const onContractCardExpand = () => {
    setContractCardExpanded(!contractCardExpanded);
  };
  const [leasingOfferContractData, setLeasingOfferContractData] = useState(emptyObject);

  const [contractSelected, setContractSelected] = useState(emptyObject);

  const handleContractSelect = item => {
    setContractSelected(item);
  };

  const dispatch = useDispatch();

  const handleModificationChange = useCallback(
    value => {
      setContractSelected({
        ...contractSelected,
        modificationTypeId: value ? value.offerTypeId : emptyString,
        modificationTypeName: value ? value.offerTypeName : emptyString
      });
    },
    [contractSelected]
  );

  useEffect(() => {
    actions.loadOfferTypes(documentTypeIds.OfferModification);
  }, [actions]);

  useEffect(() => {
    if (!documentId && !siteId) return;
    dispatch(loadLeasingOfferContractData(documentId, siteId)).then(data => {
      setLeasingOfferContractData(data);
    });
  }, [documentId, siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (offerTypeId) {
      const obj = modificationTypes?.find(a => a.offerTypeId === offerTypeId);
      setModificationName(obj?.offerTypeName);
    }
  }, [offerTypeId, modificationTypes]);

  return (
    <>
      <CollapseCard
        id={'contractCard'}
        icon={Description}
        expanded={contractCardExpanded}
        onToggle={onContractCardExpand}
        toggleOnHeaderClick
        title={
          isNewContractModification
            ? t('RestructuringEdit.Contract.Title', { no: emptyString })
            : `${t('RestructuringEdit.Contract.Title', {
                no: leasingOfferContractData?.contractNumber || emptyString
              })}`
        }
        subheader={
          <ContractSummary
            leasingOfferContractData={leasingOfferContractData}
            contractSelected={contractSelected}
            modificationName={modificationName}
          />
        }
        hideSubheaderOnExpand
      >
        <ContractComponent
          openContractSelection={openContractSelection}
          leasingOfferContractData={leasingOfferContractData ? leasingOfferContractData : emptyObject}
          isNewContractModification={isNewContractModification}
          contractSelected={contractSelected}
          modificationTypes={modificationTypes}
          onModificationChange={handleModificationChange}
          offerTypeId={offerTypeId}
          onChangeOpenReinitializationConfirmBool={onChangeOpenReinitializationConfirmBool}
          reinitializationProcessing={reinitializationProcessing}
        />
      </CollapseCard>
      {!isNewContractModification && (
        <CollapseCard
          id={'contractCard'}
          icon={List}
          title={t('RestructuringEdit.Contract.Details')}
          content={
            <ContractDetailInfoComponent leasingOfferContractDataDetails={leasingOfferContractData?.contractDetails} toggleOnHeaderClick />
          }
        />
      )}
      {showContractSelection && (
        <ContractSelectionContainer
          showContractSelection={showContractSelection}
          onCloseModal={closeContractSelection}
          onSelectContract={handleContractSelect}
          contractSelected={contractSelected}
        />
      )}
    </>
  );
};

ContractCard.propTypes = {
  isNewContractModification: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  openContractSelection: PropTypes.func.isRequired,
  closeContractSelection: PropTypes.func.isRequired,
  showContractSelection: PropTypes.bool,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  offerTypeId: PropTypes.number,
  modificationTypes: PropTypes.array,
  actions: PropTypes.object.isRequired,
  onChangeOpenReinitializationConfirmBool: PropTypes.func,
  reinitializationProcessing: PropTypes.bool
};

function mapStateToProps(state, _ownProps) {
  const modificationTypes = getDocumentSubtypes(state, documentTypeIds.OfferModification);
  const offerTypeId = getOfferType(state);
  return { modificationTypes, offerTypeId };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ loadOfferTypes }, dispatch)
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(ContractCard);
